import {
  Activity,
  Bell,
  Building2,
  CreditCard,
  Gauge,
  KeyRound,
  type LucideIcon,
  Palette,
  PlugZap,
  Shield,
  TrendingUp,
  UserRound,
  Users,
  Wrench,
} from 'lucide-react';
import {
  type WorkspaceCapabilities,
  type WorkspaceSummary,
  getWorkspaceEntryPath,
} from '../lib/workspaces.js';

export type SidebarMode = 'app' | 'settings';

const scopedAppRouteRoots = new Set([
  'feed',
  'notifications',
  'spec',
  'connectors',
  'knowledge',
  'skills',
  'projects',
  'accounts',
  'contacts',
  'settings',
]);
const reservedTopLevelSections = new Set([
  '',
  'signup',
  'signin',
  'login',
  'auth',
  'api',
  'settings',
  'feed',
  'notifications',
  'spec',
  'connectors',
  'knowledge',
  'skills',
  'projects',
  'accounts',
  'contacts',
  'workspaces',
  'workspace',
  'onboarding',
  'docs',
  'pricing',
  'privacy',
  'terms',
  'health',
]);

export function getWorkspaceSlugFromPathname(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const [firstSegment, secondSegment] = segments;

  if (!firstSegment || !secondSegment) return null;
  if (reservedTopLevelSections.has(firstSegment)) return null;
  if (!scopedAppRouteRoots.has(secondSegment)) return null;

  return decodeURIComponent(firstSegment);
}

export function getWorkspaceScopedPath(pathname: string, to: `/${string}`): string {
  const workspaceSlug = getWorkspaceSlugFromPathname(pathname);
  return workspaceSlug ? `/${encodeURIComponent(workspaceSlug)}${to}` : to;
}

export function getSidebarMode(pathname: string): SidebarMode {
  return /(^|\/)settings(\/|$)/.test(pathname) ? 'settings' : 'app';
}

export function getSidebarWorkspaceInitial(name: string): string {
  return name.trim().charAt(0).toLowerCase() || 'p';
}

// The active row comes from the active workspace query, which settings edits keep current.
export function getSwitchableWorkspaces<T extends Pick<WorkspaceSummary, 'name' | 'slug'>>(
  listed: T[] | undefined,
  active: T | null,
): T[] {
  const others = (listed ?? []).filter((workspace) => workspace.slug !== active?.slug);
  return [...others, ...(active ? [active] : [])].sort((a, b) => a.name.localeCompare(b.name));
}

export function getWorkspaceSwitchPath(
  workspace: Pick<WorkspaceSummary, 'slug' | 'onboardingComplete'>,
): string {
  return workspace.onboardingComplete
    ? getWorkspaceEntryPath(workspace.slug)
    : `/${encodeURIComponent(workspace.slug)}/welcome`;
}

export interface CapabilityGatedNavItem {
  capability?: keyof WorkspaceCapabilities;
  /** Hidden unless the Spec surface is visible to this build and workspace. */
  specOnly?: boolean;
}

export type SettingsNavChild = { label: string; to: `/${string}` };
export type SettingsNavItem = {
  label: string;
  to: `/${string}`;
  icon: LucideIcon;
  capability?: keyof WorkspaceCapabilities;
  specOnly?: boolean;
  /** A collapsible sub-menu under the item; the item itself links to the first child. */
  children?: readonly SettingsNavChild[];
};

export type SettingsNavGroup = {
  label: string;
  items: readonly SettingsNavItem[];
  /** Whole group hidden unless the Spec surface is visible. */
  specOnly?: boolean;
};

/**
 * The settings sidebar, in render order.
 *
 * Lives here rather than in `app-sidebar.tsx` so the order and the gating can
 * be asserted in a plain node-environment unit test beside
 * `filterSettingsNavGroups`, without pulling the whole sidebar (and
 * `@parcel/ui`) into that test.
 *
 * `Spec AI > Tools` is deliberately NOT capability-gated. `canManageSpecTools`
 * is owner/admin-only, but every member has to reach this page to manage their
 * own personal tool overrides, so gating the item would lock members out of
 * their own settings. The GROUP is gated on Spec surface visibility instead
 * (`specOnly`), matching the app-mode `Spec` item.
 */
export const settingsNav: readonly SettingsNavGroup[] = [
  {
    label: 'Personal',
    items: [
      { label: 'Profile', to: '/settings/profile', icon: UserRound },
      { label: 'Notifications', to: '/settings/notifications', icon: Bell },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { label: 'General', to: '/settings/general', icon: Building2 },
      { label: 'Members', to: '/settings/members', icon: Users },
      { label: 'Signals', to: '/settings/signals', icon: Activity },
      {
        label: 'Scoring',
        to: '/settings/scoring/accounts',
        icon: TrendingUp,
        children: [
          { label: 'Accounts', to: '/settings/scoring/accounts' },
          { label: 'Projects', to: '/settings/scoring/projects' },
          { label: 'Contacts', to: '/settings/scoring/contacts' },
        ],
      },
      { label: 'Security', to: '/settings/security', icon: Shield },
      { label: 'Billing', to: '/settings/billing', icon: CreditCard },
      { label: 'Usage', to: '/settings/usage', icon: Gauge },
      { label: 'Appearance', to: '/settings/appearance', icon: Palette },
    ],
  },
  {
    label: 'Spec AI',
    specOnly: true,
    items: [{ label: 'Tools', to: '/settings/spec-ai/tools', icon: Wrench }],
  },
  {
    label: 'Developer',
    items: [
      {
        label: 'API Keys',
        to: '/settings/api-keys',
        icon: KeyRound,
        capability: 'canManageApiKeys',
      },
      { label: 'MCP', to: '/settings/developer/mcp', icon: PlugZap },
    ],
  },
];

// null capabilities = workspace still loading → gated items stay hidden, so
// members never see a flash of the admin-only tabs.
//
// `specVisible` is required rather than defaulted: a new caller that forgets it
// should fail to compile, not quietly publish the Spec group in a build where
// Spec is dark.
export function filterSettingsNavGroups<
  Item extends CapabilityGatedNavItem,
  Group extends { items: readonly Item[]; specOnly?: boolean },
>(
  groups: readonly Group[],
  capabilities: WorkspaceCapabilities | null,
  options: { specVisible: boolean },
): Array<Group & { items: Item[] }> {
  return groups
    .filter((group) => !group.specOnly || options.specVisible)
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.specOnly || options.specVisible) &&
          (!item.capability || Boolean(capabilities?.[item.capability])),
      ),
    }));
}

/**
 * Whether a top-level nav item is the place the member is. Spec is the one
 * exception to a plain path match: with a chat named in the URL, the chat's own
 * row is the active place, and the Spec item (which always opens a fresh chat)
 * is not.
 */
export function isSidebarNavItemActive(input: {
  to: string;
  pathname: string;
  chatOpen: boolean;
}): boolean {
  const { to, pathname, chatOpen } = input;
  const onPath = pathname === to || pathname.startsWith(`${to}/`);
  if (!onPath) return false;
  return to.endsWith('/spec') ? !chatOpen : true;
}
